import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { ChevronLeft, ChevronRight, Clock, Check, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
];

// Locale mapping for date formatting
const localeMap = {
  en: 'en-US',
  de: 'de-DE',
  sk: 'sk-SK',
  cs: 'cs-CZ'
};

const BookingModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    areaOfInterest: '',
    phone: '',
    address: ''
  });

  const [errors, setErrors] = useState({});

  // Fetch booked slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchBookedSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchBookedSlots = async (date) => {
    setIsLoading(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await axios.get(`${API}/consultations/slots/${dateStr}`);
      setBookedSlots(response.data.booked_slots || []);
    } catch (error) {
      
      setBookedSlots([]);
    }
    setIsLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('booking.errors.nameRequired');
    if (!formData.email.trim()) newErrors.email = t('booking.errors.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('booking.errors.emailInvalid');
    }
    if (!formData.companyName.trim()) newErrors.companyName = t('booking.errors.companyRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await axios.post(`${API}/consultations`, {
        date: dateStr,
        time_slot: selectedTime,
        name: formData.name,
        email: formData.email,
        company_name: formData.companyName,
        area_of_interest: formData.areaOfInterest || null,
        phone: formData.phone || null,
        address: formData.address || null,
        language: language // Send language for localized emails
      });
      
      if (response.data) {
        setBookingSuccess(true);
        setStep(4);
      }
    } catch (error) {
      
      alert(t('booking.errors.bookingFailed'));
    }
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData({
      name: '',
      email: '',
      companyName: '',
      areaOfInterest: '',
      phone: '',
      address: ''
    });
    setErrors({});
    setBookingSuccess(false);
    onClose();
  };

  const isTimeSlotAvailable = (time) => {
    return !bookedSlots.includes(time);
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = date.getDay();
    // Disable weekends and past dates
    return date < today || day === 0 || day === 6;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const locale = localeMap[language] || 'en-US';
    return date.toLocaleDateString(locale, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0d1442]">
            {step === 4 ? t('booking.confirmed') : t('booking.title')}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        {step < 4 && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step >= s ? 'bg-[#1a237e] text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-1 ${step > s ? 'bg-[#1a237e]' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step 1: Select Date */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-slate-600 text-center mb-4">{t('booking.selectDate')}</p>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={isDateDisabled}
                className="rounded-lg border"
              />
            </div>
            <div className="flex justify-end mt-4">
              <Button 
                onClick={() => setStep(2)}
                disabled={!selectedDate}
                className="btn-gradient text-white px-6 py-2 rounded-lg"
              >
                {t('booking.continue')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Select Time */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={() => setStep(1)}
                className="flex items-center text-[#1a237e] hover:underline"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('booking.back')}
              </button>
              <p className="text-slate-600">{formatDate(selectedDate)}</p>
            </div>
            
            <p className="text-slate-600 text-center mb-4">{t('booking.selectTime')}</p>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#1a237e]" />
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {timeSlots.map((time) => {
                  const available = isTimeSlotAvailable(time);
                  return (
                    <button
                      key={time}
                      onClick={() => available && setSelectedTime(time)}
                      disabled={!available}
                      className={`py-3 px-4 rounded-lg border text-sm font-medium transition-all ${
                        selectedTime === time
                          ? 'bg-[#1a237e] text-white border-[#1a237e]'
                          : available
                            ? 'bg-white text-slate-700 border-slate-200 hover:border-[#1a237e]'
                            : 'bg-slate-100 text-slate-400 border-slate-100 cursor-not-allowed line-through'
                      }`}
                    >
                      <Clock className="w-4 h-4 mx-auto mb-1" />
                      {time}
                    </button>
                  );
                })}
              </div>
            )}
            
            <div className="flex justify-end mt-4">
              <Button 
                onClick={() => setStep(3)}
                disabled={!selectedTime}
                className="btn-gradient text-white px-6 py-2 rounded-lg"
              >
                {t('booking.continue')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Contact Details */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={() => setStep(2)}
                className="flex items-center text-[#1a237e] hover:underline"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('booking.back')}
              </button>
              <p className="text-slate-600 text-sm">
                {formatDate(selectedDate)} {t('booking.at')} {selectedTime}
              </p>
            </div>

            <div className="space-y-4">
              {/* Required Fields */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                  {t('booking.requiredInfo')}
                </h3>
                
                <div>
                  <Label htmlFor="name">{t('booking.fullName')} *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="email">{t('booking.emailAddress')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@company.com"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <Label htmlFor="companyName">{t('booking.companyName')} *</Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Your Company"
                    className={errors.companyName ? 'border-red-500' : ''}
                  />
                  {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
                </div>
              </div>

              {/* Optional Fields */}
              <div className="space-y-3 pt-4 border-t">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                  {t('booking.optionalInfo')}
                </h3>

                <div>
                  <Label htmlFor="areaOfInterest">{t('booking.areaOfInterest')}</Label>
                  <Textarea
                    id="areaOfInterest"
                    name="areaOfInterest"
                    value={formData.areaOfInterest}
                    onChange={handleInputChange}
                    placeholder={t('booking.areaPlaceholder')}
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="phone">{t('booking.phone')}</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+421 XXX XXX XXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">{t('booking.address')}</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="City, Country"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-gradient text-white px-8 py-3 rounded-lg"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('booking.booking')}
                  </>
                ) : (
                  t('booking.confirmBooking')
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[#0d1442] mb-2">
              {t('booking.consultationBooked')}
            </h3>
            <p className="text-slate-600 mb-4">
              {t('booking.scheduledFor')}
            </p>
            <p className="text-lg font-semibold text-[#1a237e] mb-2">
              {formatDate(selectedDate)}
            </p>
            <p className="text-lg font-semibold text-[#1a237e] mb-6">
              {t('booking.at')} {selectedTime}
            </p>
            <p className="text-sm text-slate-500 mb-6">
              {t('booking.confirmationEmail')} {formData.email}.<br />
              {t('booking.reminderNote')}
            </p>
            <Button 
              onClick={handleClose}
              className="btn-gradient text-white px-8 py-3 rounded-lg"
            >
              {t('booking.close')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
